import React, { useMemo, useRef } from 'react'

import { Canvas, useFrame } from '@react-three/fiber'
import { OrbitControls, useGLTF } from '@react-three/drei'
import { Color } from "three"


const planetGap = 500
const planets = [
  {
    'obj': 'tie',
    'colorbg': '#F5A8A8',
    'color1': '#E19999',
    'color2': '#FAB7B7'
  },
  {
    'obj': 'folder',
    'colorbg': '#B2C8DF',
    'color1': '#6E85B7',
    'color2': '#C4D7E0'
  },
  {
    'obj': 'cup',
    'colorbg': '#AEE1E1',
    'color1': '#FFDCB8',
    'color2': '#FCD1D1'
  },
  {
    'obj': 'backpack',
    'colorbg': '#BEADFA',
    'color1': '#D0BFFF',
    'color2': '#DFCCFB'
  },
  {
    'obj': 'keyboard',
    'colorbg': '#F6AD7B',
    'color2': '#F2EEE5',
    'color1': '#BE7575'
  },
];

const fragmentShader = `
uniform float u_time;

uniform vec3 u_bg;
uniform vec3 u_colorA;
uniform vec3 u_colorB;

varying vec2 vUv;

//
// Description : Array and textureless GLSL 2D simplex noise function.
//      Author : Ian McEwan, Ashima Arts.
//  Maintainer : ijm
//     Lastmod : 20110822 (ijm)
//     License : Copyright (C) 2011 Ashima Arts. All rights reserved.
//               Distributed under the MIT License. See LICENSE file.
//               https://github.com/ashima/webgl-noise
//

// https://github.com/hughsk/glsl-noise/blob/master/simplex/2d.glsl

vec3 mod289(vec3 x) {
  return x - floor(x * (1.0 / 289.0)) * 289.0;
}

vec2 mod289(vec2 x) {
  return x - floor(x * (1.0 / 289.0)) * 289.0;
}

vec3 permute(vec3 x) {
  return mod289(((x*34.0)+1.0)*x);
}

float snoise(vec2 v)
  {
  const vec4 C = vec4(0.211324865405187,  // (3.0-sqrt(3.0))/6.0
                      0.366025403784439,  // 0.5*(sqrt(3.0)-1.0)
                     -0.577350269189626,  // -1.0 + 2.0 * C.x
                      0.024390243902439); // 1.0 / 41.0
// First corner
  vec2 i  = floor(v + dot(v, C.yy) );
  vec2 x0 = v -   i + dot(i, C.xx);

// Other corners
  vec2 i1;
  //i1.x = step( x0.y, x0.x ); // x0.x > x0.y ? 1.0 : 0.0
  //i1.y = 1.0 - i1.x;
  i1 = (x0.x > x0.y) ? vec2(1.0, 0.0) : vec2(0.0, 1.0);
  // x0 = x0 - 0.0 + 0.0 * C.xx ;
  // x1 = x0 - i1 + 1.0 * C.xx ;
  // x2 = x0 - 1.0 + 2.0 * C.xx ;
  vec4 x12 = x0.xyxy + C.xxzz;
  x12.xy -= i1;

// Permutations
  i = mod289(i); // Avoid truncation effects in permutation
  vec3 p = permute( permute( i.y + vec3(0.0, i1.y, 1.0 ))
    + i.x + vec3(0.0, i1.x, 1.0 ));

  vec3 m = max(0.5 - vec3(dot(x0,x0), dot(x12.xy,x12.xy), dot(x12.zw,x12.zw)), 0.0);
  m = m*m ;
  m = m*m ;

// Gradients: 41 points uniformly over a line, mapped onto a diamond.
// The ring size 17*17 = 289 is close to a multiple of 41 (41*7 = 287)

  vec3 x = 2.0 * fract(p * C.www) - 1.0;
  vec3 h = abs(x) - 0.5;
  vec3 ox = floor(x + 0.5);
  vec3 a0 = x - ox;

// Normalise gradients implicitly by scaling m
// Approximation of: m *= inversesqrt( a0*a0 + h*h );
  m *= 1.79284291400159 - 0.85373472095314 * ( a0*a0 + h*h );

// Compute final noise value at P
  vec3 g;
  g.x  = a0.x  * x0.x  + h.x  * x0.y;
  g.yz = a0.yz * x12.xz + h.yz * x12.yw;
  return 130.0 * dot(m, g);
}
// End of Simplex Noise Code


void main() {
  vec3 color = u_bg;

  float noise1 = snoise(vUv);
  float noise2 = snoise((vUv + vec2(u_time)) * 0.5);

  color = mix(color, u_colorA, noise1);
  color = mix(color, u_colorB, noise2);
  
  gl_FragColor = vec4(color ,1.0);
}

`
const vertexShader = `
varying vec2 vUv;

void main() {
  vUv = uv;

  vec4 modelPosition = modelMatrix * vec4(position, 1.0);
  vec4 viewPosition = viewMatrix * modelPosition;
  vec4 projectedPosition = projectionMatrix * viewPosition;

  gl_Position = projectedPosition;
}
`

const Planet = (props) => {
  const { nodes } = useGLTF(props.obj);
  const ref = useRef();
  const rotation_speed = Math.random() * 0.01;
  const radius_speed = Math.random() * 0.2;
  const radius = planetGap * (props.index + 1);
  const offset = Math.random() * 2 * Math.PI;
  const inclination = Math.random() * Math.PI / 3;

  useFrame(({ clock }) => {
    ref.current.material.uniforms.u_time.value = clock.getElapsedTime();
    ref.current.rotation.y += rotation_speed;
    ref.current.rotation.x += rotation_speed;
    ref.current.rotation.z += rotation_speed;
    ref.current.position.set(
    	Math.cos((clock.getElapsedTime() * radius_speed) + offset) * radius,
    	Math.sin((clock.getElapsedTime() * radius_speed) + offset) * radius * Math.sin(inclination),
    	Math.sin((clock.getElapsedTime() * radius_speed) + offset) * radius * Math.cos(inclination),
    );
  });
  const uniforms = useMemo(
    () => ({
      u_time: {
        value: 0.0,
      },
      u_bg: { value: new Color(props.colorbg) },
      u_colorA: { value: new Color(props.color1) },
      u_colorB: { value: new Color(props.color2) },
    }),
  [props.colorbg, props.color1, props.color2])

  return (
    <mesh
      ref={ref}
      scale={80}
      geometry={nodes.item.geometry}>
      <shaderMaterial
         fragmentShader={fragmentShader}
         vertexShader={vertexShader}
         uniforms={uniforms}
         wireframe={false}
       />
    </mesh>
  );
};

const SolarSystem = (props) => {
  return (
    <React.Fragment>
      {planets.map((meta, index) => (
        <React.Suspense key={index} fallback={null}>
          <Planet 
            obj={`/meshes/${meta["obj"]}.glb`}
            colorbg={meta["colorbg"]}
            color1={meta["color1"]}
            color2={meta["color2"]}
            index={index}/>
        </React.Suspense>
      ))}
    </React.Fragment>
  );
};


function Stars({ count = 10000 }) {
  const positions = useMemo(() => {
    let positions = []
    for (let i = 0; i < count; i++) {
      const r = 4000
      const theta = 2 * Math.PI * Math.random()
      const phi = Math.acos(2 * Math.random() - 1)
      const x = r * Math.cos(theta) * Math.sin(phi) + (-2500 + Math.random() * 5000)
      const y = r * Math.sin(theta) * Math.sin(phi) + (-2500 + Math.random() * 5000)
      const z = r * Math.cos(phi) + (-1000 + Math.random() * 2000)
      positions.push(x)
      positions.push(y)
      positions.push(z)
    }
    return new Float32Array(positions)
  }, [count])
  return (
    <points>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" count={positions.length / 3} array={positions} itemSize={3}/>
      </bufferGeometry>
      <pointsMaterial size={5} sizeAttenuation color="white" fog={false} />
    </points>
  )
}


const Space = (props) => {
    const cam_pos = [500 + Math.random() * 3000, 500 + Math.random() * 3000, 500 + Math.random() * 3000];
    const orbit_tar = [-1500 + Math.random() * 3000, -1500 + Math.random() * 3000, -1500 + Math.random() * 3000];

    return (
      <Canvas camera={{position:cam_pos, near:.1, far:100000}}>
        <OrbitControls target={orbit_tar} maxDistance={4000} minDistance={1}/>
        <ambientLight intensity={0.01}/>
        <Stars />
        <SolarSystem />
      </Canvas>
    )
};

export default Space;
